/**
 * CountrySelector Component
 * 
 * Dropdown country selector with grouped regions (Europe, Americas, Asia Pacific, etc.)
 * Integrates with vatDisplay.js for Tax ID type auto-detection.
 */

import { h } from 'preact'
import { EU_COUNTRIES, getTaxIdType, getCountryName } from '../utils/vatDisplay.js'

/**
 * Full list of countries grouped by region
 */
export const COUNTRY_GROUPS = {
  'European Union': [
    { code: 'AT', name: 'Austria' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'EE', name: 'Estonia' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'DE', name: 'Germany' },
    { code: 'GR', name: 'Greece' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IT', name: 'Italy' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MT', name: 'Malta' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'RO', name: 'Romania' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'ES', name: 'Spain' },
    { code: 'SE', name: 'Sweden' },
  ],
  'Other Europe': [
    { code: 'GB', name: 'United Kingdom' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'NO', name: 'Norway' },
    { code: 'IS', name: 'Iceland' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'RS', name: 'Serbia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'AL', name: 'Albania' },
    { code: 'TR', name: 'Turkey' },
  ],
  'Americas': [
    { code: 'US', name: 'United States' },
    { code: 'CA', name: 'Canada' },
    { code: 'MX', name: 'Mexico' },
    { code: 'BR', name: 'Brazil' },
    { code: 'AR', name: 'Argentina' },
    { code: 'CL', name: 'Chile' },
    { code: 'CO', name: 'Colombia' },
    { code: 'PE', name: 'Peru' },
  ],
  'Asia Pacific': [
    { code: 'JP', name: 'Japan' },
    { code: 'KR', name: 'South Korea' },
    { code: 'CN', name: 'China' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'HK', name: 'Hong Kong' },
    { code: 'SG', name: 'Singapore' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'TH', name: 'Thailand' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'PH', name: 'Philippines' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'IN', name: 'India' },
    { code: 'AU', name: 'Australia' },
    { code: 'NZ', name: 'New Zealand' },
  ],
  'Middle East & Africa': [
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'IL', name: 'Israel' },
    { code: 'EG', name: 'Egypt' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'KE', name: 'Kenya' },
  ],
};

/**
 * Flat list of all countries for validation
 */
export function getAllCountries() {
  const all = [];
  Object.values(COUNTRY_GROUPS).forEach(countries => {
    countries.forEach(c => all.push(c));
  });
  return all;
}

/**
 * Flat alphabetically-sorted country list (by English name).
 *
 * Used by forms that prefer an intuitive A→Z list over region groups.
 */
export const COUNTRIES_SORTED = getAllCountries()
  .slice()
  .sort((a, b) => a.name.localeCompare(b.name, 'en'));

/**
 * Check if a country code is valid
 */
export function isValidCountryCode(code) {
  return getAllCountries().some(c => c.code === code?.toUpperCase());
}

/**
 * CountrySelector component
 */
export function CountrySelector({ 
  value, 
  onChange, 
  required = false, 
  disabled = false,
  showTaxIdHint = false,
  className = '',
  id = 'country',
  name = 'country'
}) {
  const handleChange = (e) => {
    const newCountry = e.target.value;
    if (onChange) {
      onChange(newCountry);
    }
  };

  const currentTaxIdType = value ? getTaxIdType(value) : null;
  const isEU = value && EU_COUNTRIES.includes(value.toUpperCase());

  return (
    <div className={`country-selector ${className}`}>
      <select
        id={id}
        name={name}
        value={value || ''}
        onChange={handleChange}
        required={required}
        disabled={disabled}
        className="country-selector__select"
      >
        <option value="">Select Country</option>
        
        {Object.entries(COUNTRY_GROUPS).map(([groupName, countries]) => (
          <optgroup key={groupName} label={groupName}>
            {countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </optgroup>
        ))}
      </select>

      {showTaxIdHint && value && (
        <div className="country-selector__tax-hint">
          {isEU ? (
            <span className="country-selector__eu-hint">
              🇪🇺 EU Country — VAT ID {currentTaxIdType?.required ? 'required' : 'recommended'} for reverse-charge
            </span>
          ) : (
            <span className="country-selector__non-eu-hint">
              Tax ID: {currentTaxIdType?.label || 'Optional'}
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Inline styles (can be overridden by CSS)
 */
const styles = {
  select: {
    width: '100%',
    padding: '14px 16px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.15)',
    background: 'rgba(0,0,0,0.4)',
    color: '#fff',
    fontSize: '15px',
    cursor: 'pointer',
  },
  hint: {
    fontSize: '12px',
    marginTop: '6px',
    padding: '4px 8px',
    borderRadius: '4px',
  },
  euHint: {
    color: '#81c784',
    background: 'rgba(76, 175, 80, 0.1)',
  },
  nonEuHint: {
    color: '#999',
  }
};

export default CountrySelector;
